import { useEffect, useState, useRef, useCallback, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation, Link } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";

// Cookies power our auth: every /api call needs to send the httpOnly
// session_token cookie. Setting this at App boot means we can drop the
// per-request `withCredentials: true` and the legacy Bearer-from-localStorage
// helpers without touching every call site.
axios.defaults.withCredentials = true;

// Pages
import LandingPage from "@/pages/LandingPage";
import LandingPageV2 from "@/pages/LandingPageV2";
import LandingPageV3 from "@/pages/LandingPageV3";
import LandingPageV4 from "@/pages/LandingPageV4";
import LandingPageV5 from "@/pages/LandingPageV5";
import PreviewAllVersionsPage from "@/pages/PreviewAllVersionsPage";
import JoinPage from "@/pages/JoinPage";
import FounderPage from "@/pages/FounderPage";
import DirectoryPage from "@/pages/DirectoryPage";
import BusinessProfilePage from "@/pages/BusinessProfilePage";
import MyCardsPage from "@/pages/MyCardsPage";
import BlinterestPage from "@/pages/BlinterestPage";
import BlinterestHomePage from "@/pages/BlinterestHomePage";
import PinDetailPage from "@/pages/PinDetailPage";
import BoardsPage from "@/pages/BoardsPage";
import BoardDetailPage from "@/pages/BoardDetailPage";
import { PrivacyPage, TermsPage, ContactPage } from "@/pages/LegalPages";
import ProductDetailPage from "@/pages/ProductDetailPage";
import CartPage from "@/pages/CartPage";
import CheckoutPage from "@/pages/CheckoutPage";
import CheckoutSuccessPage from "@/pages/CheckoutSuccessPage";
import DashboardPage from "@/pages/DashboardPage";
import OrdersPage from "@/pages/OrdersPage";
import ProfilePage from "@/pages/ProfilePage";
import EventsPage from "@/pages/EventsPage";
// import MarketplacePage from "@/pages/MarketplacePage"; // temporarily hidden — /marketplace redirects to /blinterest. Re-enable when un-hiding.
import BusinessAcademyPage from "@/pages/BusinessAcademyPage";
import CuratorProfilePage from "@/pages/CuratorProfilePage";
import NewsPage from "@/pages/NewsPage";
import NewsArticlePage from "@/pages/NewsArticlePage";
import SavedArticlesPage from "@/pages/SavedArticlesPage";
import AdminPage from "@/pages/AdminPage";
import PricingPage from "@/pages/PricingPage";
import FoundingMembersPage from "@/pages/FoundingMembersPage";
import FoundingCharterPage from "@/pages/FoundingCharterPage";
import FoundingBusinessPage from "@/pages/FoundingBusinessPage";
import FoundingSuccessPage from "@/pages/FoundingSuccessPage";
import SubscriptionSuccessPage from "@/pages/SubscriptionSuccessPage";
import PreviewDesignPage from "@/pages/PreviewDesignPage";
import CategoryLandingPage from "@/pages/CategoryLandingPage";
import FeaturedPage from "@/pages/FeaturedPage";
import ClaimPage from "@/pages/ClaimPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import ScrollToTopButton from "@/components/ScrollToTopButton";
import StatesPage from "@/pages/StatesPage";
import JoinRedirectPage from "@/pages/JoinRedirectPage";
import ConnectReturnPage from "@/pages/ConnectReturnPage";
import ConnectRefreshPage from "@/pages/ConnectRefreshPage";
import InstallPWA from "@/components/InstallPWA";
import OwnerAcquisitionBanner from "@/components/OwnerAcquisitionBanner";
import PageviewTracker from "@/components/PageviewTracker";
import CookieConsentBanner from "@/components/CookieConsentBanner";
import SupportChatBubble from "@/components/SupportChatBubble";
import MobileBottomNav from "@/components/MobileBottomNav";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

// Auth Provider
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Refs (not state) so we don't retrigger re-renders when deduping. See
  // `checkAuth` below — multiple components call `checkAuth()` on mount and
  // React 18 StrictMode double-invokes effects, previously producing 6-8
  // /auth/me hits per page load. We now collapse concurrent + short-lived
  // repeat calls into a single request.
  const inFlightRef = useRef(null);
  const lastFetchedAtRef = useRef(0);
  // Any two calls within this window reuse the cached user — set high enough
  // to swallow mount storms but low enough that post-login refreshes still
  // work (checkAuth passes { force: true } from AuthDialog after login).
  const CACHE_TTL_MS = 5000;

  const checkAuth = useCallback(async ({ force = false } = {}) => {
    const now = Date.now();
    if (!force && now - lastFetchedAtRef.current < CACHE_TTL_MS) {
      return; // recent enough — skip the network hit
    }
    if (inFlightRef.current) {
      return inFlightRef.current; // one already in flight — piggy-back
    }
    const p = (async () => {
      try {
        const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
        // Endpoint returns null (200) for anonymous visitors — no console noise.
        setUser(response.data || null);
      } catch {
        // Network / server error only — still treat as anonymous.
        setUser(null);
      } finally {
        lastFetchedAtRef.current = Date.now();
        setLoading(false);
        inFlightRef.current = null;
      }
    })();
    inFlightRef.current = p;
    return p;
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = (redirectPath) => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (redirectPath && redirectPath.startsWith("/") && !redirectPath.startsWith("//")) {
      try { sessionStorage.setItem("post_login_redirect", redirectPath); } catch { /* ignore */ }
    }
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      if (process.env.NODE_ENV !== "production") console.error("Logout error:", error);
    }
    setUser(null);
    window.location.href = '/';
  };

  const becomeSeller = async () => {
    try {
      const response = await axios.post(`${API}/auth/become-seller`, {}, { withCredentials: true });
      setUser(response.data);
      return response.data;
    } catch (error) {
      if (process.env.NODE_ENV !== "production") console.error("Become seller error:", error);
      throw error;
    }
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, logout, becomeSeller, checkAuth, token: null }}>
      {children}
    </AuthContext.Provider>
  );
};

// Auth Callback Component
const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (!sessionIdMatch) {
        navigate('/');
        return;
      }

      const sessionId = sessionIdMatch[1];

      try {
        const response = await axios.post(
          `${API}/auth/session`,
          { session_id: sessionId },
          { withCredentials: true }
        );

        setUser(response.data.user);
        
        // Honor a pending redirect (e.g. coming back from /claim/:token).
        let target = "/dashboard";
        try {
          const stored = sessionStorage.getItem("post_login_redirect");
          if (stored && stored.startsWith("/") && !stored.startsWith("//")) {
            target = stored;
          }
        } catch { /* ignore */ }
        sessionStorage.removeItem("post_login_redirect");

        // Clear hash and navigate to target
        window.history.replaceState(null, '', target);
        navigate(target, { replace: true, state: { user: response.data.user } });
      } catch (error) {
        if (process.env.NODE_ENV !== "production") console.error("Auth error:", error);
        navigate('/');
      }
    };

    processAuth();
  }, [location, navigate, setUser]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-tbc-cream">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-tbc-navy border-t-tbc-gold rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-tbc-navy font-body">Signing you in...</p>
      </div>
    </div>
  );
};

// App Router
const AppRouter = () => {
  const location = useLocation();

  // Check for session_id in hash SYNCHRONOUSLY during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <>
      <OwnerAcquisitionBanner />
      <PageviewTracker />
      <SupportChatBubble />
      <MobileBottomNav />
      <Routes>
      <Route path="/" element={<LandingPageV5 />} />
      <Route path="/v1" element={<LandingPage />} />
      <Route path="/v2" element={<LandingPageV2 />} />
      <Route path="/v3" element={<LandingPageV3 />} />
      <Route path="/v4" element={<LandingPageV4 />} />
      <Route path="/v5" element={<LandingPageV5 />} />
      <Route path="/preview-all" element={<PreviewAllVersionsPage />} />
      <Route path="/join" element={<JoinPage />} />
      <Route path="/register" element={<JoinPage />} />
      <Route path="/signup" element={<JoinPage />} />
      <Route path="/founder" element={<FounderPage />} />
      <Route path="/about" element={<FounderPage />} />
      <Route path="/directory" element={<DirectoryPage />} />
      <Route path="/business/:businessId" element={<BusinessProfilePage />} />
      <Route path="/product/:productId" element={<ProductDetailPage />} />
      <Route path="/cart" element={<CartPage />} />
      <Route path="/checkout" element={<CheckoutPage />} />
      <Route path="/checkout/success" element={<CheckoutSuccessPage />} />
      <Route path="/dashboard" element={<DashboardPage />} />
      <Route path="/orders" element={<OrdersPage />} />
      <Route path="/profile" element={<ProfilePage />} />
      <Route path="/cards" element={<MyCardsPage />} />
      <Route path="/blinterest" element={<BlinterestHomePage />} />
      <Route path="/blinterest/discover" element={<BlinterestPage />} />
      <Route path="/blinterest/pin/:pinId" element={<PinDetailPage />} />
      <Route path="/blinterest/boards" element={<BoardsPage />} />
      <Route path="/blinterest/board/:boardId" element={<BoardDetailPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/events" element={<EventsPage />} />
      <Route path="/marketplace" element={<Navigate to="/blinterest" replace />} />
      <Route path="/business-academy" element={<BusinessAcademyPage />} />
      <Route path="/curator/:handle" element={<CuratorProfilePage />} />
      <Route path="/news" element={<NewsPage />} />
      <Route path="/news/saved" element={<SavedArticlesPage />} />
      <Route path="/news/:articleId" element={<NewsArticlePage />} />
      <Route path="/admin" element={<AdminPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/founding-100" element={<FoundingCharterPage />} />
      <Route path="/founding-business" element={<FoundingBusinessPage />} />
      <Route path="/founding/success" element={<FoundingSuccessPage />} />
      {/* Redirect the old /founding-members route to the new
          Founding Charter page so existing links keep working. */}
      <Route path="/founding-members" element={<Navigate to="/founding-100" replace />} />
      <Route path="/founding-members-legacy" element={<FoundingMembersPage />} />
      <Route path="/subscription/success" element={<SubscriptionSuccessPage />} />
      <Route path="/preview-design" element={<PreviewDesignPage />} />
      <Route path="/category/:slug" element={<CategoryLandingPage />} />
      <Route path="/featured" element={<FeaturedPage />} />
      <Route path="/claim/:token" element={<ClaimPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/states" element={<StatesPage />} />
      <Route path="/join" element={<JoinRedirectPage />} />
      <Route path="/connect/return" element={<ConnectReturnPage />} />
      <Route path="/connect/refresh" element={<ConnectRefreshPage />} />
    </Routes>
    </>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <InstallPWA />
        <ScrollToTopButton />
        <CookieConsentBanner />
        <Toaster position="top-right" />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
